// Turns one resolved frame + its preceding window into the personnel item
// shape LiveCanvas already knows how to draw (see LiveDashboardModal's
// useLiveData.js): {id, name, status, position_meters:{x,y}}, plus `ghost`,
// `offSite`/`method` (the position's own flags; off-site dots are drawn only
// over the map) and `device_id`/`onDuty` for the history workspace's own
// consumers (roster panel, vitals tab subject picker).

const deletedName = (person) =>
  person.deleted ? `前人員 #${person.id}` : person.name;

/**
 * @param {object|null} frame - the frame resolved by resolveFrame(), or null
 *   when nothing precedes the playhead yet.
 * @param {object[]} prevFrames - up to 5 frames preceding `frame`, nearest
 *   first (see getPrecedingFrames). Ignored when `frame` is null: there is no
 *   "current" bucket to ghost anyone forward from.
 * @param {object[]} roster - the frames response's `personnel` array.
 * @param {number} playheadMs - the playhead the frame was resolved for.
 * @param {number} intervalMs - the bucket size (intervalUsed * 1000).
 * @returns {object[]} one entry per roster member seen at most 5 buckets
 *   before the playhead. STALENESS IS MEASURED AGAINST THE PLAYHEAD, not
 *   against frame adjacency: frames omit empty buckets, so "the latest frame
 *   <= playhead" may be hours old, and a person present in it must NOT be
 *   drawn as live at the playhead (the sticky-dot dishonesty the history
 *   workspace exists to avoid). Live only in the resolved frame's own bucket
 *   (bucketsAgo 0), ghosted for bucketsAgo 1..5, absent after.
 */
export const buildCanvasPersonnel = (
  frame,
  prevFrames,
  roster,
  playheadMs = null,
  intervalMs = null,
) => {
  if (!Array.isArray(roster) || roster.length === 0) return [];

  const candidates = frame ? [frame, ...(prevFrames || [])] : [];

  // Buckets between a frame's slot and the playhead; 0 = the playhead sits
  // inside this frame's own bucket. Without playhead/interval context
  // (defensive: stray caller, tests written pre-staleness) everything in the
  // resolved frame counts as current.
  const bucketsAgo = (f) => {
    if (playheadMs == null || !intervalMs || intervalMs <= 0) {
      return f === frame ? 0 : 1;
    }
    return Math.floor((playheadMs - f.t) / intervalMs);
  };

  const result = [];
  for (const person of roster) {
    const pid = String(person.id);

    for (const candidate of candidates) {
      const position = candidate.positions?.[pid];
      if (!position) continue;

      const ago = bucketsAgo(candidate);
      if (ago > 5) break; // older candidates are only older still: absent.
      result.push({
        id: person.id,
        name: deletedName(person),
        device_id: person.device_id,
        onDuty: person.onDuty,
        status: candidate.status?.[pid] || "offline",
        position_meters: { x: position.x, y: position.y },
        ghost: ago > 0,
        // From the frame the dot comes from, so a ghost keeps its own flags.
        offSite: !!position.offSite,
        method: position.method ?? null,
      });
      break;
    }
    // no candidate within 5 buckets: absent entirely, not shown.
  }
  return result;
};
